import { JSX } from 'react';
import AppLoader from '@/features/dashboard/_root/components/AppLoader.tsx';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { Navigate } from '@tanstack/react-router';
import { appRoute } from '@/features/dashboard/_root/routes/appRoute.tsx';
import { resolveEntryTarget } from '@/features/dashboard/_root/access/entryTarget.ts';
import { readStoredCompanyId } from '@/features/dashboard/_root/access/currentCompany.ts';

export const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { user, authLoading } = useAuth();
  const userAccessData = appRoute.useLoaderData();

  // 1. Show the loader if we are waiting for Firebase
  if (authLoading) {
    return <AppLoader />;
  }

  // 2. If auth check is done and there's no user, redirect to login.
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  // 3. If user exists but email is not verified, redirect.
  if (!user.emailVerified) {
    return <Navigate to="/auth/email-verification" replace />;
  }

  // 4. Company or invitations decide where the user lands.
  const target = resolveEntryTarget(userAccessData, readStoredCompanyId());
  if (target === 'complete-profile') {
    return <Navigate to="/auth/complete-profile" replace />;
  }
  if (target === 'workspaces') {
    return <Navigate to="/auth/workspaces" replace />;
  }

  // 5. If all checks pass, render the requested child component.
  return children;
};
